import React, { useState, useEffect } from 'react';
import {
  MessageCircle,
  Share2,
  Eye,
  Clock,
  CheckCircle2,
  User,
  FileText,
  Award,
  Calendar,
  ChevronDown,
  ChevronRight,
  Bell,
  X,
  Trash2
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';

const StudentSharingDashboard = () => {
  const { currentUser } = useFamilyContext();
  const [activeTab, setActiveTab] = useState('overview');
  const [sharedItems, setSharedItems] = useState([]);
  const [comments, setComments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [replyText, setReplyText] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    if (currentUser) {
      console.log('📘 Student dashboard loading for user:', currentUser);
      loadAllData();

      // Set up real-time subscription for new comments
      const setupSubscription = async () => {
        const { data: studentMember } = await supabase
          .from('family_members')
          .select('id')
          .eq('auth_user_id', currentUser.id)
          .single();

        if (!studentMember) return;

        // Get all sharing permissions for this student
        const { data: permissions } = await supabase
          .from('sharing_permissions')
          .select('id')
          .eq('student_id', studentMember.id);

        if (!permissions || permissions.length === 0) return;

        const permissionIds = permissions.map(p => p.id);

        // Subscribe to new comments on student's shared items
        const subscription = supabase
          .channel('student-comments')
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'parent_comments',
              filter: `sharing_permission_id=in.(${permissionIds.join(',')})`
            },
            (payload) => {
              console.log('📫 New comment received:', payload);
              toast('New feedback received!', {
                icon: '💬',
                duration: 4000
              });
              // Reload comments to show the new one
              loadComments();
            }
          )
          .subscribe();

        return () => {
          subscription.unsubscribe();
        };
      };

      setupSubscription();
    }
  }, [currentUser]);

  const loadAllData = async () => {
    setLoading(true);
    console.log('📘 Loading all data for student...');
    await Promise.all([
      loadSharedItems(),
      loadComments(),
      loadNotifications()
    ]);
    setLoading(false);
  };

  const loadSharedItems = async () => {
    try {
      // Get student's family member record
      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!studentMember) return;

      // Get all shared items
      const { data: permissions, error } = await supabase
        .from('sharing_permissions')
        .select(`
          *,
          shared_with:family_members!shared_with_id(
            id,
            name,
           
            role
          )
        `)
        .eq('student_id', studentMember.id)
        .is('revoked_at', null)
        .order('shared_at', { ascending: false });

      if (error) {
        console.error('Error loading shared items:', error);
        return;
      }

      setSharedItems(permissions || []);
    } catch (error) {
      console.error('Error in loadSharedItems:', error);
    }
  };

  const loadComments = async () => {
    try {
      // Get student's family member record
      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!studentMember) return;

      // Get all comments on student's shared items
      // First get the sharing permissions for this student
      const { data: permissions } = await supabase
        .from('sharing_permissions')
        .select('id')
        .eq('student_id', studentMember.id);

      if (!permissions || permissions.length === 0) {
        setComments([]);
        return;
      }

      const permissionIds = permissions.map(p => p.id);

      // Now get comments for those permissions
      const { data: studentComments, error } = await supabase
        .from('parent_comments')
        .select(`
          *,
          parent:family_members!parent_id(
            id,
            name
          ),
          sharing_permission:sharing_permissions!sharing_permission_id(
            item_type,
            item_title,
            item_id,
            student_id
          )
        `)
        .in('sharing_permission_id', permissionIds)
        .order('created_at', { ascending: false });

      console.log('📘 Student comments loaded:', { studentComments, error, permissionIds });

      if (error) {
        console.error('Error loading comments:', error);
        return;
      }

      // Filter out null sharing_permissions (happens if permission was revoked)
      const validComments = (studentComments || []).filter(c => c.sharing_permission);
      setComments(validComments);

      // Count unread comments (comments created in last 7 days that haven't been viewed)
      const unread = validComments.filter(c => {
        const daysSince = Math.floor((Date.now() - new Date(c.created_at)) / (1000 * 60 * 60 * 24));
        return daysSince <= 7;
      }).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error in loadComments:', error);
    }
  };

  const handleReplySubmit = async (parentCommentId) => {
    const text = replyText[parentCommentId];
    if (!text?.trim()) return;

    try {
      // Get student's family_member record
      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!studentMember) {
        toast.error('Unable to add reply - student record not found');
        return;
      }

      // Get the original comment to find the sharing_permission_id
      const { data: originalComment } = await supabase
        .from('parent_comments')
        .select('sharing_permission_id')
        .eq('id', parentCommentId)
        .single();

      if (!originalComment) {
        toast.error('Unable to find original comment');
        return;
      }

      // Insert the student reply as a comment with author_type = 'student'
      const { error } = await supabase
        .from('parent_comments')
        .insert({
          sharing_permission_id: originalComment.sharing_permission_id,
          parent_id: studentMember.id, // Using parent_id field for consistency
          comment_text: text.trim(),
          author_type: 'student',
          author_id: studentMember.id
        });

      if (error) throw error;

      toast.success('Reply sent successfully');
      setReplyText(prev => ({ ...prev, [parentCommentId]: '' }));
      setReplyingTo(null);

      // Reload comments to show the new reply
      await loadComments();
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const loadNotifications = async () => {
    try {
      // Get student's family member record
      const { data: studentMember } = await supabase
        .from('family_members')
        .select('id')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (!studentMember) return;

      // Get notifications
      const { data: notifs, error } = await supabase
        .from('sharing_notifications')
        .select(`
          *,
          sender:family_members!sender_id(
            name,
          )
        `)
        .eq('recipient_id', studentMember.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error loading notifications:', error);
        return;
      }

      setNotifications(notifs || []);
    } catch (error) {
      console.error('Error in loadNotifications:', error);
    }
  };

  const handleRevokeSharing = async (permissionId) => {
    if (!window.confirm('Are you sure you want to revoke sharing for this item?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('sharing_permissions')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', permissionId);

      if (error) throw error;

      toast.success('Sharing revoked successfully');
      await loadSharedItems();
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Failed to revoke sharing');
    }
  };

  const toggleItemExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getItemTypeIcon = (itemType) => {
    switch (itemType) {
      case 'essay':
        return <FileText className="w-5 h-5 text-azure-600" />;
      case 'brag_sheet':
      case 'teacher_brag_sheet':
        return <Award className="w-5 h-5 text-violet-600" />;
      default:
        return <FileText className="w-5 h-5 text-stone-600" />;
    }
  };

  const getItemTypeColor = (itemType) => {
    switch (itemType) {
      case 'essay':
        return 'bg-azure-50 text-azure-700 border-azure-200';
      case 'brag_sheet':
      case 'teacher_brag_sheet':
        return 'bg-violet-50 text-violet-700 border-violet-200';
      default:
        return 'bg-stone-50 text-stone-700 border-stone-200';
    }
  };

  const OverviewTab = () => {
    const recentComments = comments.slice(0, 5);
    const stats = {
      totalShared: sharedItems.length,
      totalComments: comments.length,
      parentsWithAccess: [...new Set(sharedItems.map(i => i.shared_with_id))].length,
      recentActivity: sharedItems[0]?.shared_at
    };

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-2">
              <Share2 className="text-azure-500" size={24} />
              <span className="text-2xl font-bold">{stats.totalShared}</span>
            </div>
            <p className="text-sm text-stone-600">Items Shared</p>
          </div>

          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-2">
              <MessageCircle className="text-violet-500" size={24} />
              <span className="text-2xl font-bold">{stats.totalComments}</span>
            </div>
            <p className="text-sm text-stone-600">Comments Received</p>
          </div>

          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-2">
              <User className="text-emerald-500" size={24} />
              <span className="text-2xl font-bold">{stats.parentsWithAccess}</span>
            </div>
            <p className="text-sm text-stone-600">Parents with Access</p>
          </div>

          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-2">
              <Clock className="text-orange-500" size={24} />
              <span className="text-sm font-semibold">
                {stats.recentActivity
                  ? formatDistanceToNow(new Date(stats.recentActivity), { addSuffix: true })
                  : 'No activity'}
              </span>
            </div>
            <p className="text-sm text-stone-600">Last Shared</p>
          </div>
        </div>

        {/* Recent Comments */}
        <div className="bg-white rounded-xl border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <MessageCircle className="text-violet-500" size={20} />
                Recent Parent Comments
              </h3>
              {unreadCount > 0 && (
                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-sm font-medium">
                  {unreadCount} new
                </span>
              )}
            </div>
          </div>
          <div className="divide-y">
            {recentComments.length === 0 ? (
              <div className="p-6 text-center text-stone-500">
                No comments yet. Share items with your parents to get their feedback!
              </div>
            ) : (
              recentComments.map(comment => (
                <div key={comment.id} className="p-6 hover:bg-stone-50 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-stone-900">
                            {comment.parent?.name || 'Parent'}
                          </p>
                          <p className="text-sm text-stone-500 mt-1">
                            On: {comment.sharing_permission?.item_title ||
                                 comment.sharing_permission?.item_type?.replace(/_/g, ' ')}
                          </p>
                        </div>
                        <span className="text-xs text-stone-500">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <div className="mt-3 text-stone-700 whitespace-pre-wrap break-words">
                        {comment.comment_text}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          {recentComments.length > 0 && (
            <div className="p-4 border-t bg-stone-50">
              <button
                onClick={() => setActiveTab('comments')}
                className="text-sm text-azure-600 hover:text-azure-700 font-medium"
              >
                View all comments →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const SharedItemsTab = () => (
    <div className="space-y-4">
      {sharedItems.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Share2 className="mx-auto h-12 w-12 text-stone-300 mb-4" />
          <p className="text-stone-600">You haven't shared any items yet.</p>
        </div>
      ) : (
        sharedItems.map(item => (
          <div key={item.id} className="bg-white rounded-xl border overflow-hidden">
            <div
              className="p-6 cursor-pointer hover:bg-stone-50 transition-colors"
              onClick={() => toggleItemExpanded(item.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {getItemTypeIcon(item.item_type)}
                  <div>
                    <h3 className="font-semibold text-stone-900">
                      {item.item_title || item.item_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                      <span>Shared with: {item.shared_with?.name}</span>
                      <span>•</span>
                      <span>{format(new Date(item.shared_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {item.allow_comments && (
                    <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs rounded-full">
                      Comments enabled
                    </span>
                  )}
                  {expandedItems[item.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </div>
              </div>
            </div>

            {expandedItems[item.id] && (
              <div className="border-t px-6 py-4 bg-stone-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-stone-600">
                    <p>Permission level: <span className="font-medium">{item.permission_level}</span></p>
                    {item.viewed_at && (
                      <p>Viewed: {format(new Date(item.viewed_at), 'MMM d, yyyy at h:mm a')}</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRevokeSharing(item.id)}
                    className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm"
                  >
                    <Trash2 size={16} />
                    Revoke Access
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );

  const CommentsTab = () => {
    // Group comments by sharing_permission_id to show conversations together
    const groupedComments = comments.reduce((acc, comment) => {
      const key = comment.sharing_permission_id;
      if (!acc[key]) {
        acc[key] = {
          permission: comment.sharing_permission,
          thread: []
        };
      }
      acc[key].thread.push(comment);
      return acc;
    }, {});

    // Sort threads by most recent activity
    const sortedThreads = Object.entries(groupedComments)
      .sort(([,a], [,b]) => {
        const aLatest = Math.max(...a.thread.map(c => new Date(c.created_at).getTime()));
        const bLatest = Math.max(...b.thread.map(c => new Date(c.created_at).getTime()));
        return bLatest - aLatest;
      });

    return (
      <div className="space-y-4">
        {sortedThreads.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <MessageCircle className="mx-auto h-12 w-12 text-stone-300 mb-4" />
            <p className="text-stone-600">No comments yet.</p>
            <p className="text-sm text-stone-500 mt-2">
              When parents comment on your shared items, they'll appear here.
            </p>
          </div>
        ) : (
          sortedThreads.map(([permissionId, { permission, thread }]) => (
            <div key={permissionId} className="bg-white rounded-xl border overflow-hidden">
              {/* Item header */}
              <div className="px-6 py-4 bg-stone-50 border-b">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    getItemTypeColor(permission?.item_type)
                  }`}>
                    {getItemTypeIcon(permission?.item_type)}
                    {permission?.item_title || permission?.item_type?.replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              {/* Conversation thread */}
              <div className="p-6 space-y-4">
                {thread.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map(comment => (
                  <div key={comment.id} className={`flex items-start gap-4 ${
                    comment.author_type === 'student' ? 'ml-12' : ''
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      comment.author_type === 'student'
                        ? 'bg-azure-100'
                        : 'bg-gradient-to-br from-violet-100 to-violet-200'
                    }`}>
                      <User className={`w-5 h-5 ${
                        comment.author_type === 'student' ? 'text-azure-700' : 'text-violet-700'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-stone-900">
                            {comment.author_type === 'student'
                              ? 'You'
                              : (comment.parent?.name || 'Parent')}
                          </h4>
                        </div>
                        <span className="text-xs text-stone-500">
                          {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        comment.author_type === 'student'
                          ? 'bg-azure-50'
                          : 'bg-stone-50'
                      }`}>
                        <div className="text-stone-700 whitespace-pre-wrap break-words">
                          {comment.comment_text}
                        </div>
                      </div>

                      {/* Reply button for parent comments */}
                      {comment.author_type !== 'student' && (
                        <div className="mt-2">
                          {replyingTo === comment.id ? (
                            <div className="flex items-start gap-2">
                              <textarea
                                value={replyText[comment.id] || ''}
                                onChange={(e) => setReplyText(prev => ({
                                  ...prev,
                                  [comment.id]: e.target.value
                                }))}
                                placeholder="Type your reply..."
                                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                                rows={2}
                                autoFocus
                              />
                              <button
                                onClick={() => handleReplySubmit(comment.id)}
                                disabled={!replyText[comment.id]?.trim()}
                                className="px-3 py-1.5 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:bg-stone-300 text-sm"
                              >
                                Send
                              </button>
                              <button
                                onClick={() => {
                                  setReplyingTo(null);
                                  setReplyText(prev => ({ ...prev, [comment.id]: '' }));
                                }}
                                className="px-3 py-1.5 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300 text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setReplyingTo(comment.id)}
                              className="text-sm text-azure-600 hover:text-azure-700 font-medium"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-azure-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-stone-900">Sharing Dashboard</h1>
            <p className="text-stone-600 mt-2">
              Manage what you've shared with parents and view their feedback
            </p>
          </div>
          {unreadCount > 0 && (
            <div className="relative">
              <Bell className="w-6 h-6 text-stone-600" />
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <div className="flex gap-6">
          {['overview', 'shared-items', 'comments'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-azure-600 text-azure-600'
                  : 'border-transparent text-stone-600 hover:text-stone-900'
              }`}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'shared-items' && `Shared Items (${sharedItems.length})`}
              {tab === 'comments' && `Comments (${comments.length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'shared-items' && <SharedItemsTab />}
      {activeTab === 'comments' && <CommentsTab />}
    </div>
  );
};

export default StudentSharingDashboard;